import queryString from 'query-string';
import { matchPath } from 'react-router';

import * as types from 'constants/ActionTypes';
import { PATH } from 'constants/RoutesConstants';
import { ALL_STATS_PATHS } from 'constants/StatisticsConstants';
import { STATISTICS_LOCATION_CHANGE } from 'modules/statistics/actionTypes';
import { createReducer } from 'utils/ReduxUtils';

/**
 * @typedef {{
 *  selectedBrandIds: Array<string>,
 *  brandIds: Array<string>,
 *  sort: string,
 *  size: number,
 *  displayAllInterventions: boolean,
 *  periodHasChangedCount: number,
 *  page: number,
 *  modules: Array<import("@arhs-fleetback/types/entities/interventions").InterventionStatus>,
 *  dateField: 'createdOn' | 'sendDate',
 *  hasVideo: boolean,
 * }} StatisticsFiltersReducer
 */

/** @type {StatisticsFiltersReducer} */
export const initialState = {
  selectedBrandIds: [],
  brandIds: [],
  sort: 'dealerName,asc',
  size: 10,
  displayAllInterventions: true,
  periodHasChangedCount: 0,
  modules: [],
  page: 0,
  dateField: 'createdOn',
  hasVideo: false,
};

const manageDateField = (state, locationPath) => {
  const isAfterSalesPath = [
    PATH.STATS_AFTER_SALES,
    PATH.STATS_AFTER_SALES_DETAILS,
    PATH.MANUFACTURER_STATS_AFTER_SALES,
    PATH.MANUFACTURER_STATS_AFTER_SALES_DETAILS,
    PATH.SELF_SERVICE_STATS_DASHBOARD,
    PATH.SELF_SERVICE_STATS_DETAILS,
    PATH.MANUFACTURER_STATS_SELF_SERVICE,
    PATH.MANUFACTURER_STATS_SELF_SERVICE_DETAILS,
  ].some(path => matchPath(path.path, locationPath));

  const replaceCreatedOn = state.dateField === 'createdOn' && isAfterSalesPath;

  return {
    ...state,
    ...(replaceCreatedOn ? { dateField: 'startDate' } : {})
  };
};

/**
 * @param {StatisticsFiltersReducer} state
 * @param {import('modules/global/generic.types').LocationChangeAction} action
 * @returns {StatisticsFiltersReducer}
 */
const onLocationChange = (state, action) => {
  const { payload } = action;
  const fbPath = ALL_STATS_PATHS.find(path => matchPath(path.path, payload.location.pathname));
  const isDashboard = fbPath !== undefined;

  if (isDashboard) {
    if (payload.isFirstRendering) {
      /** @type {{ modules?: string | Array<import("@arhs-fleetback/types/entities/interventions").InterventionStatus>, [field:string]: any}} */
      const { modules: paramModule = [] } = queryString.parse(payload.location.search);

      /** @type {Array<import("@arhs-fleetback/types/entities/interventions").InterventionStatus>} */
      const modules = Array.isArray(paramModule) ? paramModule : paramModule?.split(',');

      return {
        ...manageDateField(state, payload.location.pathname),
        modules,
      };
    }
    return resetFilters(state, action);
  }
  return manageDateField(state, payload.location.pathname);
};

/**
 * @param {StatisticsFiltersReducer} state
 * @param {import('modules/global/generic.types').LocationChangeAction} action
 * @returns
 */
const resetFilters = (state, { payload }) => {
  const skipReset = payload.location.state?.persistFilter || payload.action === 'POP';
  let newState = state;

  if (!skipReset) {
    newState = initialState;
  }
  return manageDateField(newState, payload.location.pathname);
};

const afterSelectDealer = state => ({
  ...state,
  page: 0,
  brandIds: [],
});

/**
 * @param {StatisticsFiltersReducer} state
 * @param {import('modules/statistics/types').SetDealersStatsFiltersAction} action
 * @returns {StatisticsFiltersReducer}
 */
const afterSetDealerStatisticsFilter = (state, { payload }) => {
  const newState = {
    ...state,
    ...payload,
  };

  if (newState.page !== payload.page) {
    newState.page = 0;
  }

  return newState;
};

export default createReducer(initialState, {
  [types.SELECT_DEALER]: afterSelectDealer,
  [STATISTICS_LOCATION_CHANGE]: onLocationChange,
  [types.SET_DEALER_STATISTICS_PERIOD]: afterSetDealerStatisticsFilter,
  [types.SET_DEALER_STATISTICS_FILTERS]: afterSetDealerStatisticsFilter,
});
